import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import CryptoJS from 'crypto-js';
import { slugify } from 'Utils/Common';
import logger from 'Utils/logger';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const mykey = process.env.REACT_APP_AES_KEY;

/**
 * Initialises Firebase Cloud Messaging after the service worker is ready.
 * Requests notification permission, obtains the FCM token, registers it with
 * the backend, and attaches a foreground message handler.
 *
 * @param {ServiceWorkerRegistration} swRegistration - The active SW registration.
 * @param {Object} opts
 * @param {Function} opts.firebaseRegisterUser - Apollo lazy-query executor for FIREBASE_REGISTER_USER.
 * @param {string}   opts.userId
 */
export async function setupFirebaseMessaging(swRegistration, { firebaseRegisterUser, userId }) {
    try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            throw new Error('Notification permission denied');
        }
        logger.log('Firebase: Notification permission granted.');

        const app = initializeApp(firebaseConfig);
        const messaging = getMessaging(app);

        const firebase_token = await getToken(messaging, {
            serviceWorkerRegistration: swRegistration,
        });

        logger.log('Firebase Token:', firebase_token);
        window.firebase_token = firebase_token;
        localStorage.setItem('firebase_token', firebase_token);

        firebaseRegisterUser({
            variables: { user_id: userId, firebase_token, device: 'web' },
            fetchPolicy: 'network-only',
        });

        onMessage(messaging, (payload) => {
            logger.log('firebase:foreground:', payload);
            _handleForegroundMessage(payload);
        });
    } catch (error) {
        logger.error('Error setting up Firebase messaging:', error);
    }
}

// ---------------------------------------------------------------------------
// Internal: foreground push notification display
// ---------------------------------------------------------------------------

function _handleForegroundMessage(payload) {
    const { fcm_type } = payload.data;

    if (fcm_type === 'jitsi_ring_send') {
        if (Date.now() > parseInt(payload.data.xmpp_unix) + 60000) return;
        _showNotification(
            payload.data.user_fullname + ' is calling you...',
            {
                body: 'Click to receive the call',
                data: {
                    conversation_id: payload.data.conversation_id,
                    receiver_id: payload.data.receiver_id,
                    ring_index: payload.data.ring_index,
                    BASE_URL: payload.data.BASE_URL,
                    origin: payload.data.origin,
                },
            }
        );
        return;
    }

    if (fcm_type === 'delete_msg') {
        const msg_body = _decryptBody(payload.data.msg_body_str ?? '');
        _showNotification(
            payload.data.sendername + ' has deleted a message.',
            {
                body: msg_body,
                icon: payload.data.file_server + 'profile-pic/Photos/' + payload.data.sender_img,
                data: {
                    origin: payload.data.origin,
                    fcm_type,
                    url: `/connect/${slugify(payload.data.conv_title)}`,
                },
            }
        );
        return;
    }

    // Shared shape for new_message, edit_message, new_reply_message, edit_reply_message
    const urlMap = {
        new_message: `/connect/${slugify(payload.data.conv_title)}`,
        edit_message: `/connect/${slugify(payload.data.conv_title)}`,
        new_reply_message: `/connect/${slugify(payload.data.conv_title)}/reply/${payload.data.reply_for_msgid}`,
        edit_reply_message: `/connect/${slugify(payload.data.conv_title)}/reply/${payload.data.reply_for_msgid}`,
    };

    if (!(fcm_type in urlMap)) return;
    if (payload.data.msg_type === 'call') return;

    const msg_body = _decryptBody(payload.data.msg_body_str);
    const title =
        fcm_type === 'edit_reply_message'
            ? payload.data.sendername + ' has edited a reply message:'
            : payload.data.msg_title;

    _showNotification(title, {
        body: msg_body,
        icon: payload.data.file_server + 'profile-pic/Photos/' + payload.data.sender_img,
        data: { origin: payload.data.origin, url: urlMap[fcm_type] },
    });
}

function _decryptBody(encrypted) {
    return CryptoJS.AES.decrypt(encrypted, mykey).toString(CryptoJS.enc.Utf8).replace(/"/g, '');
}

function _showNotification(title, options) {
    navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, options);
    });
}
